import { useState, useEffect } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PwaInstallBanner = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed before
    const dismissed = localStorage.getItem("pwa-banner-dismissed");
    if (dismissed) return;

    // iOS detection
    const ua = navigator.userAgent;
    const iosDevice = /iphone|ipad|ipod/i.test(ua);
    const notInStandalone = !(window.navigator as Navigator & { standalone?: boolean }).standalone;
    if (iosDevice && notInStandalone) {
      setIsIos(true);
      setShowBanner(true);
      return;
    }

    // Android / Chrome: listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setIsInstalled(true);
    }
    setShowBanner(false);
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem("pwa-banner-dismissed", "1");
  };

  if (!showBanner || isInstalled) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-amber-200 shadow-lg px-4 py-3 flex items-center gap-3 md:hidden">
      <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
        <img
          src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b8685d651cf0232fd706d280f6405113.jpeg"
          alt="SME"
          className="w-8 h-8 rounded-lg object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">Install SME Factory App</p>
        {isIos ? (
          <p className="text-xs text-gray-500 mt-0.5">
            Tap <span className="inline-block w-4 h-4 align-middle"><i className="ri-share-forward-line text-amber-500" /></span> then &ldquo;Add to Home Screen&rdquo;
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-0.5">Add to home screen for quick access</p>
        )}
      </div>
      {!isIos && (
        <button
          onClick={handleInstall}
          className="whitespace-nowrap flex-shrink-0 bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer"
        >
          Install
        </button>
      )}
      <button
        onClick={handleDismiss}
        className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-gray-400 cursor-pointer"
        aria-label="Dismiss"
      >
        <i className="ri-close-line text-base" />
      </button>
    </div>
  );
};

export default PwaInstallBanner;